import { useEffect, useState } from "react";
import { ChevronRight, ChevronUp, Plus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/utils/common.utils";

interface ContractSectionProps {
  icon: LucideIcon;
  title: string;
  description: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  forceOpen?: boolean;
  toggleLabel?: string;
}

/** Collapsed content stays mounted (only `hidden`), so react-hook-form keeps every value. */
export default function ContractSection({ icon: Icon, title, description, children, defaultOpen = false, forceOpen = false, toggleLabel }: ContractSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  // Latch open on an error, so fixing it doesn't snap the card shut mid-edit.
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  const expanded = open || forceOpen;
  return (
    <section className="rounded-xl border border-edge bg-white">
      <button
        type="button"
        onClick={() => setOpen(!expanded)}
        aria-expanded={expanded}
        className="flex w-full items-start gap-3 p-4 text-left sm:gap-4 sm:px-6 sm:py-5"
      >
        <Icon className="mt-0.5 size-5 shrink-0 text-moss sm:size-6" strokeWidth={1.5} />
        <div className="min-w-0 flex-1">
          <h4 className="font-display text-lg tracking-tight text-ink sm:text-xl">{title}</h4>
          <div className="mt-0.5 text-sm text-stone">{description}</div>
        </div>
        {toggleLabel && !expanded
          ? <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-moss">{toggleLabel} <ChevronRight className="size-4" /></span>
          : expanded
            ? <ChevronUp className="mt-1 size-5 shrink-0 text-stone" />
            : <Plus className="mt-1 size-5 shrink-0 text-moss" />}
      </button>
      <div className={cn("px-4 pb-5 sm:px-6 sm:pb-6", !expanded && "hidden")}>{children}</div>
    </section>
  );
}

/** Calmer sentence-case labels inside the redesigned contract modals. */
export const CONTRACT_LABEL_CLASSES =
  "[&_label]:normal-case [&_label]:tracking-normal [&_label]:text-[13px] [&_label]:font-medium [&_label]:text-ink";
